import { startTransition, useEffect, useState } from 'react';
import { type WithTestId } from '@pkgs/components/types/types';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { type BigDecimalRangeDto, UNIT_SYMBOL_MAP } from '@ev-tech/search-modules-api';

import { type FilterDropdownProps } from './FilterDropdown.types';
import { RangeInput } from '../RangeInputs/RangeInput';
import { RangeDropdown } from '../RangeDropdown/RangeDropdown';
import { FormattedNumberInput } from '../FormattedNumberInput/FormattedNumberInput';
import { formatRangeLabel } from '../../utils/stringUtils';
import { validateRangeValues } from '../../utils/numberFormatters';
import { useAppContext } from '../../hooks/useAppContext';

import rangeInputStyles from '../RangeInputs/RangeInput.module.css';

export type SurfaceFilterDropdownProps = WithTestId<FilterDropdownProps>;

export const SurfaceFilterDropdown = ({ className, 'data-testid': dataTestId }: SurfaceFilterDropdownProps) => {
  const {
    t,
    options: { measurementSystem, language },
  } = useAppContext();
  const totalSurface = useSearchStore((state) => state.totalSurface);
  const setTotalSurface = useSearchStore((state) => state.setTotalSurface);

  const [range, setRange] = useState<BigDecimalRangeDto | undefined>(totalSurface ? validateRangeValues(totalSurface) : undefined);
  const unitSymbol = UNIT_SYMBOL_MAP[measurementSystem];

  useEffect(() => {
    setRange(totalSurface ? validateRangeValues(totalSurface) : undefined);
  }, [totalSurface]);

  return (
    <RangeDropdown
      className={className}
      range={range}
      label={t('search.searchbar.surface')}
      value={totalSurface ? formatRangeLabel({ value: totalSurface, unit: unitSymbol, language, shouldFormatNumbers: true }) : ''}
      onDone={() => {
        if (range) startTransition(() => setTotalSurface(range));
      }}
      onReset={() => {
        setRange(undefined);
        startTransition(() => setTotalSurface(undefined));
      }}
      data-testid={dataTestId ? `${dataTestId}_dropdown` : 'search-components_surface-filter_dropdown'}
    >
      <RangeInput
        label={t('search.searchbar.surface')}
        id="surface"
        inputComponent={
          <FormattedNumberInput
            language={language}
            endAdornment={<Copy className={rangeInputStyles.endAdornment} as="span" size="l" text={unitSymbol} />}
          />
        }
        hasLabelHidden={true}
        defaultRange={range}
        onChange={setRange}
        data-testid={dataTestId ?? 'search-components_surface-filter'}
      />
    </RangeDropdown>
  );
};
